import { Component, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
    ClassNames,
    Color,
    forLifeOf,
    LifecycleAware,
    Masks,
    Unmasks
} from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import { LsqListSource } from '@lsq-frontend/angular-shared/api-service';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map, pluck } from 'rxjs/operators';
import { ListRelationshipsForBulkFeeStructureDto } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/list-relationships-for-bulk-fee-structure-dto';

import {
    BulkUpdateRelationshipFeeStructureRequest,
    BulkUpdateRelationshipFeeStructureService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-supplier-bulk-actions/bulk-update-relationship-fee-structure';
import {
    ListRelationshipsForBulkActionRequest,
    ListRelationshipsForBulkFeeStructureService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-supplier-bulk-actions/list-relationships-for-bulk-fee-structure';
import { FeeType } from '../../models';
import { AdminSuppliersBulkActionsService } from './bulk-actions.service';

@LifecycleAware()
@Component({
    selector: 'admin-suppliers-bulk-fee',
    template: `
        <bulk-action-layout
            [pageHeader]="'Set Fee Structure'"
            [supplierCount]="(relationshipKeys$ | async).length"
            [confirmButtonText]="'Set Fee Structure'"
            [disableConfirm]="
                !checkableRowsService.checkedRows.length ||
                this.programTypeFormGroup.controls.feePercent.invalid
            "
            (confirmClickHandler)="submitUpdate()"
        >
            <div supplier-table>
                <lsq-data-table [dataSource]="dataSource" [isPaginated]="true" [layoutFixed]="true">
                    <lsq-checkbox-column
                        [checkableRowsService]="checkableRowsService"
                        [column]="{
                            key: 'relationshipKey',
                            sortable: false
                        }"
                    >
                    </lsq-checkbox-column>
                    <lsq-column
                        [column]="{
                            key: 'supplierName',
                            header: 'Supplier Name',
                            sortable: false,
                            truncateTextCss: true
                        }"
                    >
                    </lsq-column>
                    <lsq-column
                        [column]="{
                            key: 'buyerName',
                            header: 'Buyer Relationship',
                            sortable: false,
                            truncateTextCss: true
                        }"
                    >
                    </lsq-column>
                    <lsq-column
                        [column]="{
                            header: 'Program Type',
                            key: 'programType',
                            sortable: false,
                            width: '13%'
                        }"
                    >
                        <ng-template [custom-cell] let-row="row">
                            <div *ngIf="!!!row.programType">
                                <div>
                                    Decaying Fee
                                </div>
                            </div>
                        </ng-template>
                    </lsq-column>
                    <lsq-column
                        [column]="{
                            key: 'appliedDecayingFeeRatePercent',
                            header: 'Current Rate',
                            sortable: false,
                            className: classNames.AlignRight
                        }"
                    >
                        <ng-template [custom-cell] let-row="row">
                            <div *ngIf="!isNumber(row.appliedDecayingFeeRatePercent)">
                                --
                            </div>
                            <div *ngIf="isNumber(row.appliedDecayingFeeRatePercent)">
                                {{ row.appliedDecayingFeeRatePercent }}%
                            </div>
                        </ng-template>
                    </lsq-column>
                </lsq-data-table>
            </div>

            <div confirm-card>
                <p class="data-label">Update Fee Structure</p>
                <p class="header" [style.marginBottom]="'2rem'">
                    Program Type
                </p>
                <p>
                    The fee rate program type to be used for the supplier, set to whole percentage
                    up to four decimal places.
                </p>
                <form class="control-inputs" [formGroup]="programTypeFormGroup">
                    <lsq-select
                        [disableControl]="true"
                        name="feeType"
                        [options]="feeTypeOptions"
                        formControlName="feeType"
                        [group]="programTypeFormGroup"
                    ></lsq-select>
                    <lsq-input
                        name="feePercent"
                        formControlName="feePercent"
                        [group]="programTypeFormGroup"
                        placeholder="Percent"
                        [textFormatter]="formatBpsInput"
                        [handleKeypress]="handleBpsInput"
                        [mask]="hbpsAsPercentMask"
                        [appendIcon]="[icons.FaPercent, color.GraphicGray]"
                    >
                    </lsq-input>
                </form>

                <p class="body">
                    This action will set an override for the current fee structure settings
                    displayed in the provided table for each selected supplier.
                </p>
            </div>
        </bulk-action-layout>
    `
})
export class AdminSuppliersBulkFeeComponent implements OnDestroy {
    public feeTypeOptions = [
        {
            label: 'Daily Decaying Fee Rate',
            value: FeeType.DAILY_DECAYING_FEE
        }
    ];
    public icons = Icons;
    public color = Color;
    public classNames = ClassNames;
    public hbpsAsPercentMask = Masks.HbpsAsPercentMask;

    public relationshipKeys$ = this._adminSuppliersBulkActionsService.relationshipKeys$;

    public checkableRowsService = this._adminSuppliersBulkActionsService.checkableRowsService;

    public _skipSubject = new BehaviorSubject<number>(0);
    public _takeSubject = new BehaviorSubject<number>(10);
    public skip$ = this._skipSubject.asObservable();
    public take$ = this._takeSubject.asObservable();

    private _relationshipKeysToFetch$ = combineLatest([
        this.relationshipKeys$,
        this.skip$,
        this.take$
    ]).pipe(
        map(([relationshipKeys, skip, take]) =>
            !!relationshipKeys ? relationshipKeys.slice(skip, skip + take) : []
        )
    );

    // This is an unconventional data source, because we have not yet built out a backend that
    // keeps track of which rows we have checked, only the frontend knows. So we have to do manual
    // pagination by passing the relationshipKeys we want data for to the endpoint.
    public dataSource = new LsqListSource<ListRelationshipsForBulkFeeStructureDto>({
        data$: this._listRelationshipsForBulkFeeStructure.dataStream,
        rows$: this._listRelationshipsForBulkFeeStructure.dataStream.pipe(pluck('rows')),
        error$: this._listRelationshipsForBulkFeeStructure.errorStream,
        loading$: this._listRelationshipsForBulkFeeStructure.isInFlightStream,
        totalCount$: this.relationshipKeys$.pipe(map((relationships) => relationships?.length)),
        skip: 0,
        take: 10,
        handleSkipChange: (skip) => {
            this.dataSource.skip = skip;
            this._skipSubject.next(skip);
        },
        handleTakeChange: (take) => {
            this.dataSource.take = take;
            this._takeSubject.next(take);
        }
    });

    public programTypeFormGroup = new FormGroup({
        feeType: new FormControl(null, [Validators.required]),
        feePercent: new FormControl(null, [Validators.required])
    });

    constructor(
        private _adminSuppliersBulkActionsService: AdminSuppliersBulkActionsService,
        private _listRelationshipsForBulkFeeStructure: ListRelationshipsForBulkFeeStructureService,
        private _bulkUpdateRelationshipFeeStructure: BulkUpdateRelationshipFeeStructureService
    ) {
        this._relationshipKeysToFetch$.pipe(forLifeOf(this)).subscribe((relationshipKeys) => {
            this._listRelationshipsForBulkFeeStructure.execute(
                new ListRelationshipsForBulkActionRequest({
                    relationshipKeys
                })
            );
            this.checkableRowsService.setCurrentPageKeys(relationshipKeys);
        });

        this.programTypeFormGroup.reset({
            feeType: FeeType.DAILY_DECAYING_FEE
        });
    }

    public ngOnDestroy() {}

    public async submitUpdate(): Promise<void> {
        this._adminSuppliersBulkActionsService.setIsSubmitting(true);

        // Need to get the Supplier and Buyer names of all checked rows,
        // since we only currently have the ones for the current page of the table.
        const checkedRelationships = (
            await this._listRelationshipsForBulkFeeStructure.executeOnce(
                new ListRelationshipsForBulkActionRequest({
                    relationshipKeys: this.checkableRowsService.checkedRows as string[]
                })
            )
        ).rows;

        try {
            await this._bulkUpdateRelationshipFeeStructure.executeOnce(
                new BulkUpdateRelationshipFeeStructureRequest({
                    relationshipKeys: checkedRelationships.map((rel) => rel.relationshipKey),
                    decayingFeeRatePercent: this.programTypeFormGroup.controls.feePercent.value
                })
            );

            this._adminSuppliersBulkActionsService.showConfirmationModal(
                `Fee Structure Set`,
                `A new fee structure has been set for the suppliers listed below. Please note that this fee structure will override any fee configuration set at the buyer level.`,
                checkedRelationships,
                {
                    feeType: this.feeTypeOptions[0].label,
                    feeRate: this.programTypeFormGroup.controls.feePercent.value
                }
            );
        } catch (error) {
            console.log(error);
        }
        this._adminSuppliersBulkActionsService.setIsSubmitting(false);
    }

    // TODO: Clean this up once inputs are rebuilt.
    public formatBpsInput(value: any): any {
        if (value === null || typeof value === 'undefined') {
            return value;
        }
        let formattedValue = value.toString().replace(Unmasks.CurrencyUnmask, '');

        if (value !== null && value !== '' && !isNaN(formattedValue)) {
            formattedValue = (+formattedValue).toFixed(4);
        }
        return formattedValue;
    }

    public handleBpsInput(event: KeyboardEvent): void {
        const pattern = /^-?[0-9\.-]/;
        const inputChar = String.fromCharCode(event.charCode);

        if (!pattern.test(inputChar)) {
            event.preventDefault();
        }
    }

    public isNumber(value: any) {
        return typeof value === 'number';
    }
}
